<template>
  <div class="login-game-root">
    <canvas ref="gameCanvas" class="game-canvas"></canvas>
  </div>
</template>

<script>
/**
 * 登录页跑酷小游戏：二次元少女向前跑（类 Chrome 小恐龙）
 * - 角色：使用素材图片中的金发双马尾洛丽塔少女精灵图（透明背景 PNG，替代原程序化绘制的角色）
 * - 无开始/结束弹窗：点击盒子内开始或再来一局，结果文字直接显示在云朵背景
 * - 得分左下角、个人最高右下角；云朵为半透明淡粉色
 * - 成绩：按当前登录账号（sessionStorage.CurUser）保存到后端 game_score 表
 */
import { saveGameScore, getBestScore } from '../api/gameScore'
import bus from '../utils/bus'
// 二次元少女精灵图（由 TimeAlbum/assets/girl.png 抠白底生成，位于 src/assets 下）
import girlSprite from '@/assets/girl-sprite.png'

// 逻辑画布尺寸（CSS 自适应缩放，物理像素按 devicePixelRatio 放大）
const W = 760
const H = 340
const GROUND_Y = 292
const GX = 110 // 女孩固定 x
const JUMP_VY = -8.5 // 起跳初速度（调低，避免跳得过高）
const GRAVITY = 0.52

export default {
  name: 'HomeGame',
  data() {
    return {
      user: {},
      bestScore: 0,
      state: 'ready', // ready | running | over
      score: 0,
      finalScore: 0,
      saveStatus: '', // '' | 'saving' | 'saved' | 'error' | 'guest'
      rewardTip: ''
    }
  },
  created() {
    this.game = {
      speed: 0,
      distance: 0,
      phase: 0,
      y: 0,
      jumpVy: 0,
      grounded: true,
      obstacles: [],
      clouds: [],
      hearts: [],
      spawnTimer: 70,
      heartTimer: 60
    }
    this.bokeh = [
      { x: 90, y: 66, r: 46, a: 0.1 },
      { x: 640, y: 100, r: 58, a: 0.08 },
      { x: 480, y: 258, r: 40, a: 0.1 },
      { x: 250, y: 238, r: 34, a: 0.08 },
      { x: 700, y: 282, r: 30, a: 0.1 }
    ]
    this.lastTime = 0
    this.rafId = 0
    this.overAt = 0
    this.ctx = null
    this.canvas = null
    this.boundKeydown = null
    this.boundPointer = null
    // 预加载二次元少女精灵图（onload 完成后 drawGirl 才会真正绘制，避免出现空白帧）
    this.girlImg = new Image()
    this.girlImg.src = girlSprite
  },
  mounted() {
    this.initUser()
    this.setupCanvas()
    this.initClouds()
    this.bindEvents()
    this.loop()
  },
  beforeDestroy() {
    cancelAnimationFrame(this.rafId)
    if (this.canvas && this.boundPointer) {
      this.canvas.removeEventListener('pointerdown', this.boundPointer)
    }
    if (this.boundKeydown) {
      window.removeEventListener('keydown', this.boundKeydown)
    }
    bus.$off('music:user-changed', this.onUserChanged)
  },
  methods: {
    // ---------------- 初始化 ----------------
    initUser() {
      try {
        this.user = JSON.parse(sessionStorage.getItem('CurUser') || '{}') || {}
      } catch (e) {
        this.user = {}
      }
      this.loadBest()
    },
    onUserChanged() {
      this.initUser()
    },
    loadBest() {
      const u = this.user
      if (!u || !u.no) {
        this.bestScore = 0
        return
      }
      getBestScore(u.no).then(res => {
        if (res && res.code === 200 && typeof res.data === 'number') {
          this.bestScore = res.data
        }
      }).catch(() => {})
    },
    setupCanvas() {
      this.canvas = this.$refs.gameCanvas
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      this.canvas.width = W * dpr
      this.canvas.height = H * dpr
      this.ctx = this.canvas.getContext('2d')
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    },
    initClouds() {
      const clouds = []
      for (let i = 0; i < 4; i++) {
        clouds.push({
          x: Math.random() * W,
          y: 40 + Math.random() * 130,
          v: 0.25 + Math.random() * 0.35,
          a: 0.35 + Math.random() * 0.25 // 半透明
        })
      }
      this.game.clouds = clouds
    },
    bindEvents() {
      this.onUserChanged = this.onUserChanged.bind(this)
      this.boundKeydown = this.onKeyDown.bind(this)
      this.boundPointer = this.onPointerDown.bind(this)
      window.addEventListener('keydown', this.boundKeydown)
      this.canvas.addEventListener('pointerdown', this.boundPointer)
      bus.$on('music:user-changed', this.onUserChanged)
    },

    // ---------------- 输入与状态 ----------------
    onKeyDown(e) {
      if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') {
        // 仅在游戏运行中拦截按键（用于跳跃）；平时不拦截，
        // 避免挡住页面自身的键盘滚动（空格/上箭头翻页）
        if (this.state === 'running') {
          e.preventDefault()
          if (e.repeat) return
          this.jump()
          return
        }
        if (e.repeat) return
        this.handleAction()
      }
    },
    onPointerDown(e) {
      e.preventDefault()
      this.handleAction()
    },
    handleAction() {
      if (this.state === 'ready') {
        this.startGame()
      } else if (this.state === 'running') {
        this.jump()
      } else if (this.state === 'over' && performance.now() - this.overAt > 500) {
        this.startGame()
      }
    },
    startGame() {
      const g = this.game
      g.speed = 3.4
      g.distance = 0
      g.phase = 0
      g.y = 0
      g.jumpVy = 0
      g.grounded = true
      g.obstacles = []
      g.hearts = []
      g.spawnTimer = 70
      g.heartTimer = 60
      this.score = 0
      this.finalScore = 0
      this.saveStatus = ''
      this.state = 'running'
    },
    jump() {
      const g = this.game
      if (!g.grounded) return
      g.grounded = false
      g.jumpVy = JUMP_VY
    },
    gameOver() {
      this.state = 'over'
      this.overAt = performance.now()
      this.finalScore = this.score
      this.saveStatus = ''
      this.rewardTip = ''
      this.saveScore()
    },

    // ---------------- 成绩上报 ----------------
    saveScore() {
      const u = this.user
      if (!u || !u.no) {
        this.saveStatus = 'guest'
        return
      }
      if (this.finalScore <= 0) return
      this.saveStatus = 'saving'
      saveGameScore({ userId: u.id, no: u.no, score: this.finalScore, gameType: 'run' }).then(res => {
        if (res && res.code === 200) {
          this.saveStatus = 'saved'
          const reward = res.data && res.data.rewardCoin
          this.rewardTip = (reward && reward > 0) ? ('超出 1500 分，奖励 ' + reward + ' 星裙币') : ''
        } else {
          this.saveStatus = 'error'
        }
        this.loadBest()
      }).catch(() => {
        this.saveStatus = 'error'
      })
    },

    // ---------------- 主循环 ----------------
    loop() {
      this.rafId = requestAnimationFrame(this.loop)
      this.update()
      this.draw()
    },
    update() {
      const g = this.game
      const now = performance.now()
      const dt = Math.min((now - (this.lastTime || now)) / 16.6667, 3)
      this.lastTime = now

      // 云朵始终飘动
      g.clouds.forEach(c => {
        c.x -= c.v * dt
        if (c.x < -90) {
          c.x = W + 90
          c.y = 40 + Math.random() * 130
        }
      })

      if (this.state !== 'running') return

      // 难度与距离
      g.speed = Math.min(3.4 + g.distance / 2600, 8.2)
      g.distance += g.speed * dt
      this.score = Math.floor(g.distance / 9)

      // 少女物理
      g.phase += dt * 0.16
      if (!g.grounded) {
        g.y += g.jumpVy * dt
        g.jumpVy += GRAVITY * dt
        if (g.y >= 0) {
          g.y = 0
          g.grounded = true
        }
      }

      // 生成障碍
      g.spawnTimer -= dt
      if (g.spawnTimer <= 0) {
        this.spawnObstacle()
        const gapPx = 210 + Math.random() * 150 + (g.speed - 3.4) * 14
        g.spawnTimer = gapPx / g.speed
      }

      // 移动障碍
      g.obstacles.forEach(o => {
        o.x -= g.speed * dt
      })
      g.obstacles = g.obstacles.filter(o => o.x + o.w > -20)

      // 碰撞检测（判定盒按精灵图尺寸调整，略小于视觉范围保留手感宽容度）
      // 角色碰撞盒（随精灵图显示尺寸放大：宽 26→40，高 58→90，中心对齐 GX）
      const gb = { x: GX - 20, y: GROUND_Y + g.y - 90, w: 35, h: 90 }
      for (let i = 0; i < g.obstacles.length; i++) {
        const o = g.obstacles[i]
        const ob = { x: o.x + 6, y: GROUND_Y - o.h + 6, w: o.w - 12, h: o.h - 10 }
        if (gb.x < ob.x + ob.w && gb.x + gb.w > ob.x && gb.y < ob.y + ob.h && gb.y + gb.h > ob.y) {
          this.gameOver()
          return
        }
      }

      // 漂浮爱心装饰
      g.heartTimer -= dt
      if (g.heartTimer <= 0) {
        g.heartTimer = 80 + Math.random() * 90
        g.hearts.push({
          x: 80 + Math.random() * (W - 200),
          y: GROUND_Y - 16,
          v: 0.35 + Math.random() * 0.3,
          s: 5 + Math.random() * 4,
          a: 0.5 + Math.random() * 0.3,
          life: 0
        })
      }
      g.hearts.forEach(h => {
        h.y -= h.v * dt
        h.life += dt
      })
      g.hearts = g.hearts.filter(h => h.y > -30 && h.life < 240)
    },
    spawnObstacle() {
      const types = ['gift', 'cupcake', 'flower']
      const type = types[Math.floor(Math.random() * types.length)]
      const defs = {
        gift: { w: 36, h: 32 },
        cupcake: { w: 32, h: 36 },
        flower: { w: 36, h: 40 }
      }
      this.game.obstacles.push(Object.assign({ type, x: W + 10 }, defs[type]))
    },

    // ---------------- 绘制 ----------------
    draw() {
      const ctx = this.ctx
      if (!ctx) return
      const g = this.game
      ctx.clearRect(0, 0, W, H)

      // 背景
      const grad = ctx.createLinearGradient(0, 0, 0, H)
      grad.addColorStop(0, 'rgba(255,255,255,0.32)')
      grad.addColorStop(1, 'rgba(255,255,255,0.06)')
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, W, H)

      // 柔和光斑
      ctx.fillStyle = 'rgb(67,219,239)'
      this.bokeh.forEach(b => {
        ctx.globalAlpha = b.a
        ctx.beginPath()
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2)
        ctx.fill()
      })
      ctx.globalAlpha = 1

      // 云朵（半透明淡粉）
      g.clouds.forEach(c => this.drawCloud(ctx, c))

      // 地面
      this.drawGround(ctx)

      // 漂浮爱心
      g.hearts.forEach(h => {
        ctx.globalAlpha = h.a * Math.min(1, h.life / 40) * Math.max(0, 1 - (h.life - 200) / 40)
        this.drawHeart(ctx, h.x, h.y, h.s, '#ff7ba9')
        ctx.globalAlpha = 1
      })

      // 障碍
      g.obstacles.forEach(o => this.drawObstacle(ctx, o))

      // 少女（精灵图）
      this.drawGirl(ctx)

      // HUD 与文字层
      this.drawHUD(ctx)
      if (this.state === 'ready') this.drawReady(ctx)
      else if (this.state === 'over') this.drawOver(ctx)
    },

    // 地面
    drawGround(ctx) {
      const g = this.game
      const band = ctx.createLinearGradient(0, GROUND_Y, 0, H)
      band.addColorStop(0, 'rgba(232,93,138,0.10)')
      band.addColorStop(1, 'rgba(232,93,138,0)')
      ctx.fillStyle = band
      ctx.fillRect(0, GROUND_Y, W, H - GROUND_Y)
      ctx.strokeStyle = 'rgba(232,93,138,0.55)'
      ctx.lineWidth = 2.5
      ctx.beginPath()
      ctx.moveTo(0, GROUND_Y)
      ctx.lineTo(W, GROUND_Y)
      ctx.stroke()
      const gap = 46
      const offset = this.state === 'running' ? g.distance % gap : 0
      ctx.strokeStyle = 'rgba(232,93,138,0.5)'
      ctx.lineWidth = 1.8
      for (let x = -offset; x < W; x += gap) {
        ctx.beginPath()
        ctx.moveTo(x, GROUND_Y)
        ctx.quadraticCurveTo(x + 3, GROUND_Y - 7, x + 6, GROUND_Y)
        ctx.quadraticCurveTo(x + 9, GROUND_Y - 5, x + 12, GROUND_Y)
        ctx.stroke()
      }
    },

    // 半透明淡粉色云朵
    drawCloud(ctx, c) {
      ctx.save()
      ctx.globalAlpha = c.a
      ctx.fillStyle = 'rgba(255,178,198,0.50)'
      ctx.beginPath()
      ctx.arc(c.x, c.y, 14, 0, Math.PI * 2)
      ctx.arc(c.x + 16, c.y - 6, 18, 0, Math.PI * 2)
      ctx.arc(c.x + 34, c.y, 13, 0, Math.PI * 2)
      ctx.arc(c.x + 17, c.y + 5, 14, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    },

    // 爱心
    drawHeart(ctx, x, y, s, color) {
      ctx.save()
      ctx.translate(x, y)
      ctx.beginPath()
      ctx.moveTo(0, s * 0.35)
      ctx.bezierCurveTo(-s, -s * 0.45, -s * 0.45, -s, 0, -s * 0.5)
      ctx.bezierCurveTo(s * 0.45, -s, s, -s * 0.45, 0, s * 0.35)
      ctx.closePath()
      ctx.fillStyle = color
      ctx.fill()
      ctx.restore()
    },

    // 障碍物
    drawObstacle(ctx, o) {
      if (o.type === 'gift') this.drawGift(ctx, o)
      else if (o.type === 'cupcake') this.drawCupcake(ctx, o)
      else this.drawFlower(ctx, o)
    },

    // 礼物盒
    drawGift(ctx, o) {
      const x = o.x
      const y = GROUND_Y - o.h
      const w = o.w
      const h = o.h
      const OUTLINE = '#8a3f5f'
      this.roundRectPath(ctx, x + 2, y + 3, w - 4, h - 5, 6)
      ctx.fillStyle = '#ffc1d4'
      ctx.fill()
      ctx.strokeStyle = OUTLINE
      ctx.lineWidth = 2
      ctx.stroke()
      ctx.fillStyle = '#ff5c8a'
      ctx.fillRect(x + w / 2 - 3, y + 3, 6, h - 6)
      ctx.fillRect(x + 3, y + h / 2 - 3, w - 6, 6)
      this.drawBow(ctx, x + w / 2, y + 2, 7, '#ff5c8a', OUTLINE)
      ctx.fillStyle = 'rgba(255,255,255,0.50)'
      ctx.beginPath()
      ctx.arc(x + w - 9, y + h / 2 - 1, 2.2, 0, Math.PI * 2)
      ctx.fill()
    },

    // 纸杯蛋糕
    drawCupcake(ctx, o) {
      const x = o.x
      const y = GROUND_Y - o.h
      const w = o.w
      const h = o.h
      const OUTLINE = '#8a3f5f'
      const cx = x + w / 2
      ctx.beginPath()
      ctx.moveTo(x + 3, y + h)
      ctx.lineTo(x + w - 3, y + h)
      ctx.lineTo(x + w - 7, y + h - 12)
      ctx.lineTo(x + 7, y + h - 12)
      ctx.closePath()
      ctx.fillStyle = '#f8a5c2'
      ctx.fill()
      ctx.strokeStyle = OUTLINE
      ctx.lineWidth = 2
      ctx.stroke()
      ctx.strokeStyle = 'rgba(138,63,95,0.4)'
      ctx.lineWidth = 1.2
      for (let i = 1; i < 4; i++) {
        const rx = x + 7 + (i * (w - 14)) / 4
        ctx.beginPath()
        ctx.moveTo(rx, y + h - 11)
        ctx.lineTo(rx - (w - 14) / 8, y + h)
        ctx.stroke()
      }
      ctx.beginPath()
      ctx.moveTo(x + 4, y + h - 13)
      ctx.quadraticCurveTo(x, y + h - 20, x + 6, y + h - 22)
      ctx.quadraticCurveTo(x + 5, y + h - 30, cx, y + h - 30)
      ctx.quadraticCurveTo(x + w - 5, y + h - 30, x + w - 6, y + h - 22)
      ctx.quadraticCurveTo(x + w, y + h - 20, x + w - 4, y + h - 13)
      ctx.quadraticCurveTo(cx, y + h - 18, x + 4, y + h - 13)
      ctx.closePath()
      ctx.fillStyle = '#fff0f5'
      ctx.fill()
      ctx.strokeStyle = OUTLINE
      ctx.lineWidth = 2
      ctx.stroke()
      ctx.fillStyle = '#e8506e'
      ctx.beginPath()
      ctx.arc(cx, y + h - 33, 4, 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = OUTLINE
      ctx.lineWidth = 1.5
      ctx.stroke()
      ctx.fillStyle = 'rgba(255,255,255,0.7)'
      ctx.beginPath()
      ctx.arc(cx - 1.2, y + h - 34.5, 1.2, 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = '#5d8a4a'
      ctx.lineWidth = 1.8
      ctx.beginPath()
      ctx.moveTo(cx + 2, y + h - 36)
      ctx.quadraticCurveTo(cx + 5, y + h - 38, cx + 4, y + h - 41)
      ctx.stroke()
    },

    // 小花盆
    drawFlower(ctx, o) {
      const x = o.x
      const y = GROUND_Y - o.h
      const w = o.w
      const h = o.h
      const OUTLINE = '#8a3f5f'
      const cx = x + w / 2
      const tops = [[cx - 8, y + 14], [cx, y + 8], [cx + 8, y + 14]]
      ctx.strokeStyle = '#6fae6f'
      ctx.lineWidth = 2
      tops.forEach(t => {
        ctx.beginPath()
        ctx.moveTo(t[0], y + 22)
        ctx.lineTo(t[0], t[1])
        ctx.stroke()
      })
      ctx.fillStyle = '#9fd6a5'
      ctx.beginPath()
      ctx.ellipse(cx - 8, y + 18, 4, 2.2, 0.6, 0, Math.PI * 2)
      ctx.fill()
      ctx.beginPath()
      ctx.ellipse(cx + 8, y + 18, 4, 2.2, -0.6, 0, Math.PI * 2)
      ctx.fill()
      ctx.beginPath()
      ctx.ellipse(cx, y + 15, 4, 2.2, 0, 0, Math.PI * 2)
      ctx.fill()
      const petals = ['#ff8fb3', '#ffd166', '#c9a0f2']
      tops.forEach((t, i) => {
        ctx.fillStyle = petals[i]
        ctx.beginPath()
        ctx.arc(t[0], t[1], 5, 0, Math.PI * 2)
        ctx.fill()
        ctx.strokeStyle = OUTLINE
        ctx.lineWidth = 1.6
        ctx.stroke()
        ctx.fillStyle = '#fff'
        ctx.beginPath()
        ctx.arc(t[0], t[1], 2, 0, Math.PI * 2)
        ctx.fill()
      })
      ctx.beginPath()
      ctx.moveTo(x + 4, y + h)
      ctx.lineTo(x + w - 4, y + h)
      ctx.lineTo(x + w - 9, y + 22)
      ctx.lineTo(x + 9, y + 22)
      ctx.closePath()
      ctx.fillStyle = '#f2b3c9'
      ctx.fill()
      ctx.strokeStyle = OUTLINE
      ctx.lineWidth = 2
      ctx.stroke()
      this.roundRectPath(ctx, x + 3, y + 18, w - 6, 6, 3)
      ctx.fillStyle = '#f7c9d8'
      ctx.fill()
      ctx.strokeStyle = OUTLINE
      ctx.lineWidth = 2
      ctx.stroke()
    },

    // ---------------- 少女（精灵图 + 简单动画） ----------------
    drawGirl(ctx) {
      const g = this.game
      const over = this.state === 'over'
      const ready = this.state === 'ready'
      const t = g.phase
      const grounded = g.grounded
      const time = performance.now()
      const img = this.girlImg

      // 精灵图绘制尺寸（脚底贴地：精灵图底边距脚底仅 1px）
      // 显示比例调大：高 64 → 100，宽 43 → 68（与精灵图 219x320 同比例）
      const DRAW_W = 60
      const DRAW_H = 100
      const FEET_GAP = 1

      // 影子（落地时实、跳跃时淡且小）
      const shadowScale = grounded ? 1 : Math.max(0.35, 1 - (-g.y) / 300)
      ctx.save()
      ctx.globalAlpha = 0.12 * shadowScale
      ctx.fillStyle = '#8a4a63'
      ctx.beginPath()
      ctx.ellipse(GX, GROUND_Y + 4, 34 * shadowScale, 7 * shadowScale, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()

      // 精灵图尚未加载完成时跳过绘制，避免出现空白帧
      if (!img || !img.complete || img.naturalWidth === 0) return

      // 跑步身体起伏 + 待机呼吸
      const runBob = grounded && !over ? Math.abs(Math.sin(t)) * -1.8 : 0
      const idleBob = ready ? Math.abs(Math.sin(time / 380)) * -1.2 : 0

      ctx.save()
      ctx.translate(GX, GROUND_Y + g.y + runBob + idleBob)

      // 身体倾斜：跑动轻微前倾、跳跃略微上扬、结束微倾
      let lean = 0
      if (over) lean = 0.02
      else if (!grounded) lean = -0.06
      else lean = Math.sin(t) * 0.04 + 0.02
      if (ready) lean += Math.sin(time / 380) * 0.02
      ctx.rotate(lean)

      // 绘制二次元少女精灵图（脚底对齐地面 GROUND_Y）
      ctx.drawImage(img, -DRAW_W / 2, -DRAW_H + FEET_GAP, DRAW_W, DRAW_H)

      ctx.restore()
    },
    drawBow(ctx, x, y, s, color, outline) {
      ctx.fillStyle = color
      ctx.strokeStyle = outline
      ctx.lineWidth = 1.4
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(x - s, y - s * 0.8)
      ctx.lineTo(x - s * 0.25, y + s * 0.1)
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(x + s, y - s * 0.8)
      ctx.lineTo(x + s * 0.25, y + s * 0.1)
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
      ctx.fillStyle = '#ff3d6e'
      ctx.beginPath()
      ctx.arc(x, y, s * 0.28, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
    },
    roundRectPath(ctx, x, y, w, h, r) {
      const rr = Math.min(r, w / 2, h / 2)
      ctx.beginPath()
      ctx.moveTo(x + rr, y)
      ctx.lineTo(x + w - rr, y)
      ctx.quadraticCurveTo(x + w, y, x + w, y + rr)
      ctx.lineTo(x + w, y + h - rr)
      ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h)
      ctx.lineTo(x + rr, y + h)
      ctx.quadraticCurveTo(x, y + h, x, y + h - rr)
      ctx.lineTo(x, y + rr)
      ctx.quadraticCurveTo(x, y, x + rr, y)
      ctx.closePath()
    },

    // ---------------- 文字与 HUD ----------------
    textWithOutline(ctx, text, x, y, font, fill, outlineWidth, align) {
      ctx.save()
      ctx.font = font
      ctx.textAlign = align || 'center'
      ctx.textBaseline = 'middle'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = 'rgba(255,255,255,0.95)'
      ctx.lineWidth = outlineWidth || 4
      ctx.strokeText(text, x, y)
      ctx.fillStyle = fill
      ctx.fillText(text, x, y)
      ctx.restore()
    },
    drawHUD(ctx) {
      // 左下角：得分
      this.drawHeart(ctx, 32, H - 26, 8, 'rgba(255,79,129,0.95)')
      this.textWithOutline(ctx, String(this.score), 48, H - 26, 'bold 26px "PingFang SC","Microsoft YaHei",sans-serif', '#c14a78', 4, 'left')
      // 右下角：个人最高（登录时显示）
      if (this.user && this.user.no) {
        const bestText = '最高 ' + this.bestScore
        ctx.font = 'bold 17px "PingFang SC","Microsoft YaHei",sans-serif'
        const bw = ctx.measureText(bestText).width
        this.textWithOutline(ctx, bestText, W - bw - 22, H - 26, 'bold 17px "PingFang SC","Microsoft YaHei",sans-serif', '#a04a6e', 4, 'left')
      }
    },
    drawReady(ctx) {
      // 无弹窗，文字直接显示在云朵背景上
      this.textWithOutline(ctx, '莹莹跑酷', W / 2, 110, 'bold 20px "PingFang SC","Microsoft YaHei",sans-serif', '#d14a7e', 6)
      this.textWithOutline(ctx, '点击开始奔跑', W / 2, 160, 'bold 12px "PingFang SC","Microsoft YaHei",sans-serif', '#9a4468', 5)
      if (!(this.user && this.user.no)) {
        this.textWithOutline(ctx, '未登录 · 成绩不会记录哦', W / 2, 198, '12px "PingFang SC","Microsoft YaHei",sans-serif', '#b08a97', 4)
      }
    },
    drawOver(ctx) {
      // 无弹窗，游戏结束内容直接显示在云朵背景上
      this.textWithOutline(ctx, '游戏结束', W / 2, 88, 'bold 20px "PingFang SC","Microsoft YaHei",sans-serif', '#d14a7e', 6)
      this.textWithOutline(ctx, '本次得分 ' + this.finalScore, W / 2, 130, 'bold 15px "PingFang SC","Microsoft YaHei",sans-serif', '#5a3040', 5)
      if (this.rewardTip) {
        this.textWithOutline(ctx, this.rewardTip, W / 2, 152, 'bold 14px "PingFang SC","Microsoft YaHei",sans-serif', '#d14a7e', 4)
      }
      let statusText = ''
      let statusColor = '#b08a97'
      if (this.saveStatus === 'saved') {
        statusText = '已记录到账号「' + (this.user.no || '') + '」'
        statusColor = '#4a9d6a'
      } else if (this.saveStatus === 'saving') {
        statusText = '正在记录成绩…'
        statusColor = '#c98a4a'
      } else if (this.saveStatus === 'error') {
        statusText = '成绩保存失败（网络异常）'
        statusColor = '#d15555'
      } else if (this.saveStatus === 'guest') {
        statusText = '未登录 · 成绩不会记录哦'
        statusColor = '#b08a97'
      }
      if (statusText) {
        this.textWithOutline(ctx, statusText, W / 2, 166, 'bold 14px "PingFang SC","Microsoft YaHei",sans-serif', statusColor, 4)
      }
      const bestText = this.user && this.user.no ? '个人最高 ' + this.bestScore + ' 分' : '登录后查看个人最高分'
      this.textWithOutline(ctx, bestText, W / 2, 200, 'bold 12px "PingFang SC","Microsoft YaHei",sans-serif', '#a04a6e', 4)
      this.textWithOutline(ctx, '点击再来一局', W / 2, 236, 'bold 12px "PingFang SC","Microsoft YaHei",sans-serif', '#c96a90', 4)
    }
  }
}
</script>

<style scoped>
.login-game-root {
  width: 100%;
  height: 100%;
}
.game-canvas {
  display: block;
  width: 100%;
  height: 100%;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
}
</style>
